import { enhancedApi } from '@store/generated/billing-entities';

/**
 * Feature-specific tag types for billing entity cache management
 */
const BILLING_ENTITY_TAG_TYPES = ['BillingEntity', 'BillingEntityList'] as const;

/**
 * Billing Entities API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
export const billingEntitiesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: BILLING_ENTITY_TAG_TYPES,
  endpoints: {
    // Admin endpoints
    getV2AdminOrgsByOrganizationIdBillingEntities: {
      providesTags: (result, _error, arg) =>
        result?.billing_entities
          ? [
              ...result.billing_entities.map(({ billing_entity_id }) => ({
                type: 'BillingEntity' as const,
                id: billing_entity_id || 'unknown',
              })),
              { type: 'BillingEntityList' as const, id: arg.organizationId },
            ]
          : [{ type: 'BillingEntityList', id: arg.organizationId }],
    },

    getV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityId: {
      providesTags: (_result, _error, arg) => [{ type: 'BillingEntity', id: arg.billingEntityId }],
    },

    postV2AdminOrgsByOrganizationIdBillingEntities: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntityList', id: arg.organizationId },
      ],
    },

    putV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntity', id: arg.billingEntityId },
        { type: 'BillingEntityList', id: arg.organizationId },
      ],
    },

    deleteV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntity', id: arg.billingEntityId },
        { type: 'BillingEntityList', id: arg.organizationId },
      ],
    },

    postV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdSyncToStedi: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntity', id: arg.billingEntityId },
      ],
    },

    // Provider endpoints
    getV2ProviderBillingEntities: {
      providesTags: result =>
        result?.billing_entities
          ? [
              ...result.billing_entities.map(({ billing_entity_id }) => ({
                type: 'BillingEntity' as const,
                id: billing_entity_id || 'unknown',
              })),
              { type: 'BillingEntityList' as const, id: 'PROVIDER' },
            ]
          : [{ type: 'BillingEntityList', id: 'PROVIDER' }],
    },

    getV2ProviderBillingEntitiesByBillingEntityId: {
      providesTags: (_result, _error, arg) => [{ type: 'BillingEntity', id: arg.billingEntityId }],
    },

    postV2ProviderBillingEntities: {
      invalidatesTags: [{ type: 'BillingEntityList', id: 'PROVIDER' }],
    },

    putV2ProviderBillingEntitiesByBillingEntityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntity', id: arg.billingEntityId },
        { type: 'BillingEntityList', id: 'PROVIDER' },
      ],
    },

    deleteV2ProviderBillingEntitiesByBillingEntityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntity', id: arg.billingEntityId },
        { type: 'BillingEntityList', id: 'PROVIDER' },
      ],
    },

    postV2ProviderBillingEntitiesByBillingEntityIdSyncToStedi: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntity', id: arg.billingEntityId },
      ],
    },
  },
});

// Inject additional custom endpoints
const billingEntitiesApiInjected = billingEntitiesApi.injectEndpoints({
  endpoints: build => ({
    setDefaultBillingEntity: build.mutation<
      import('@store/generated/billing-entities').BillingEntityResponse2,
      { billingEntityId: string }
    >({
      query: ({ billingEntityId }) => ({
        url: `/v2/provider/billing-entities/${billingEntityId}/set-default`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'BillingEntityList', id: 'PROVIDER' }],
    }),
    setDefaultBillingEntityAdmin: build.mutation<
      import('@store/generated/billing-entities').BillingEntityResponse2,
      { organizationId: string; billingEntityId: string }
    >({
      query: ({ organizationId, billingEntityId }) => ({
        url: `/v2/admin/orgs/${organizationId}/billing-entities/${billingEntityId}/set-default`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BillingEntityList', id: arg.organizationId },
      ],
    }),
  }),
});

// Export hooks with user-friendly names only (Provider endpoints)
export const {
  // Provider queries
  useGetV2ProviderBillingEntitiesQuery: useGetBillingEntitiesQuery,
  useLazyGetV2ProviderBillingEntitiesQuery: useLazyGetBillingEntitiesQuery,
  useGetV2ProviderBillingEntitiesByBillingEntityIdQuery: useGetBillingEntityQuery,
  useLazyGetV2ProviderBillingEntitiesByBillingEntityIdQuery: useLazyGetBillingEntityQuery,

  // Provider mutations
  usePostV2ProviderBillingEntitiesMutation: useCreateBillingEntityMutation,
  usePutV2ProviderBillingEntitiesByBillingEntityIdMutation: useUpdateBillingEntityMutation,
  useDeleteV2ProviderBillingEntitiesByBillingEntityIdMutation: useDeleteBillingEntityMutation,
  usePostV2ProviderBillingEntitiesByBillingEntityIdSyncToStediMutation:
    useSyncBillingEntityMutation,
  useSetDefaultBillingEntityMutation,

  // Admin queries
  useGetV2AdminOrgsByOrganizationIdBillingEntitiesQuery: useGetBillingEntitiesAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdBillingEntitiesQuery:
    useLazyGetBillingEntitiesAdminQuery,
  useGetV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdQuery:
    useGetBillingEntityAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdQuery:
    useLazyGetBillingEntityAdminQuery,

  // Admin mutations
  usePostV2AdminOrgsByOrganizationIdBillingEntitiesMutation: useCreateBillingEntityAdminMutation,
  usePutV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdMutation:
    useUpdateBillingEntityAdminMutation,
  useDeleteV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdMutation:
    useDeleteBillingEntityAdminMutation,
  usePostV2AdminOrgsByOrganizationIdBillingEntitiesAndBillingEntityIdSyncToStediMutation:
    useSyncBillingEntityAdminMutation,
  useSetDefaultBillingEntityAdminMutation,
} = billingEntitiesApiInjected;
